import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  memo,
} from 'react'

export interface Props {
  disabled?: boolean
  id?: string
  name: string
  type?: string
  placeholder?: string
  error?: boolean
  isDirty?: boolean
  required?: boolean
  variant?: Variant
  value?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
}

export const Input = memo(
  forwardRef<HTMLInputElement, Props>(function Input(
    {
      disabled,
      id,
      name,
      type,
      placeholder,
      error = false,
      isDirty = false,
      required,
      variant = 'text',
      value,
      onBlur,
      onChange,
      onFocus,
    },
    ref,
  ) {
    return (
      <Container
        ref={ref}
        error={error}
        id={id}
        isDirty={isDirty}
        disabled={disabled}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        variant={variant}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    )
  }),
)

Input.displayName = 'Input'

const Container = styled.input<ContainerProps>`
  ${({ theme, color, error, variant }) => {
    return css`
      font-family: ${theme.fontFamily.paragraph};

      :-webkit-autofill,
      :-webkit-autofill:hover,
      :-webkit-autofill:focus,
      :-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 100px black inset;
        -webkit-text-fill-color: white;
      }

      ${variant === 'submit'
        ? css`
            display: inherit;
            background-color: #54a78b;
            color: ${theme.colors.variants.neutralLight4};
            font-family: ${theme.fontFamily.paragraph};
            font-size: 0.8125rem;
            font-weight: 600;
            letter-spacing: 2px;
            line-height: 1.125rem;
            margin: 3.75rem auto 0;
            padding: 1.3125rem 1.875rem;
            text-align: center;
            text-transform: uppercase;
            transition: 0.3s ease-in-out;
            cursor: pointer;

            @media (max-width: 1023px) {
              margin-top: 2.25rem;
            }

            &:hover {
              background: #3e8970;
            }

            &:disabled {
              background-color: ${theme.colors.variants.neutralLight3};
              cursor: not-allowed;
            }
          `
        : css`
            width: 100%;
            height: 3rem;
            font-size: 0.9375rem;
            color: ${theme.colors.variants.neutralDark4};
            border-bottom: 1px solid
              ${error ? theme.colors.variants.dangerLight1 : '#EDF4F1'};
            background: rgba(237, 244, 241, 0.5);
            padding: 0 1rem;
          `}
    `
  }}
`

interface ContainerProps {
  error: boolean
  isDirty: boolean
  variant: Variant
}

export type Variant = 'text' | 'checkbox' | 'submit'
